import { useRef, useState } from "react";
import validator from "validator";
import {
  AlertCircleIcon,
  EyeIcon,
  EyeOffIcon,
  LockIcon,
  MailIcon,
} from "./AuthIcons";
import {
  SESSION_ENDING_CODES,
  sessionEndedMessage,
  signInRequest,
} from "./authApi";

const GENERIC_FAILURE = "We couldn't sign you in. Please try again.";

const signInFailureMessage = (error) => {
  if (error.code === "NETWORK" || error.code === "OTP_EMAIL_FAILED") return error.message;
  if (SESSION_ENDING_CODES.has(error.code)) return sessionEndedMessage(error);
  if (error.status === 401) return "Email or password is incorrect.";
  return GENERIC_FAILURE;
};

const emailProblem = (email) => {
  if (!email) return "Enter your email address.";
  return validator.isEmail(email) ? undefined : "Enter a valid email address.";
};

const validate = (email, password) => ({
  email: emailProblem(email),
  password: password ? undefined : "Enter your password.",
});

/** Label, leading icon, the control(s) and the inline error of one input. */
function Field({ id, label, icon, error, children }) {
  return (
    <div className="mrc-auth__field">
      <label className="mrc-auth__label" htmlFor={id}>
        {label}
      </label>
      <div className="mrc-auth__control" data-invalid={error ? "true" : undefined}>
        <span className="mrc-auth__icon">{icon}</span>
        {children}
      </div>
      {error && (
        <p id={`${id}-error`} className="mrc-auth__error">
          <AlertCircleIcon size={14} />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

/**
 * First step of sign-in: email + password.
 *
 * The password lives only in this component, so it is gone from memory the moment the page
 * moves on to the code step (or into the app).
 *
 * props
 *   email, onEmailChange  controlled by the parent so the address survives a trip to the code step
 *   notice                message to show on arrival (why the user is back here)
 *   focusPassword         arrive with the caret in the password field (after a notice)
 *   enter / leaving       slide direction while entering / leaving
 *   onSignedIn(response)  the server accepted the request; return true if the page is moving on
 *                         (code step or signed in), false if the response was not understood
 */
export default function CredentialsStep({
  email,
  onEmailChange,
  notice = "",
  focusPassword = false,
  enter,
  leaving,
  onSignedIn,
}) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(notice);
  const [submitting, setSubmitting] = useState(false);

  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const submittingRef = useRef(false);

  // Editing a field retires the messages about it (and the form-level one).
  const clearFeedback = (field) => {
    setFieldErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
    setFormError("");
  };

  const handleEmailChange = (event) => {
    onEmailChange(event.target.value);
    clearFeedback("email");
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    clearFeedback("password");
  };

  // Validate when the user leaves the field, not on every keystroke (and say nothing if empty).
  const handleEmailBlur = () => {
    const value = email.trim();
    if (value) setFieldErrors((current) => ({ ...current, email: emailProblem(value) }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;

    const cleanEmail = email.trim();
    const errors = validate(cleanEmail, password);

    setFormError("");
    setFieldErrors(errors);
    const firstInvalid = errors.email ? emailRef : errors.password ? passwordRef : null;
    if (firstInvalid) {
      firstInvalid.current?.focus();
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    let movedOn = false; // when the page is leaving, keep the form locked instead of flashing idle

    try {
      const response = await signInRequest(cleanEmail, password);
      movedOn = onSignedIn(response) === true;
      if (!movedOn) setFormError(GENERIC_FAILURE);
    } catch (error) {
      setFormError(signInFailureMessage(error));
      if (error.status === 401) {
        passwordRef.current?.focus();
        passwordRef.current?.select();
      }
    } finally {
      submittingRef.current = movedOn;
      setSubmitting(movedOn);
    }
  };

  return (
    <form
      className="mrc-auth__step"
      data-enter={enter}
      data-leaving={leaving || undefined}
      onSubmit={handleSubmit}
      noValidate
      aria-labelledby="mrc-auth-title"
      aria-busy={submitting || undefined}
    >
      <h1 id="mrc-auth-title" className="mrc-auth__title">
        Welcome back
      </h1>
      <p className="mrc-auth__lead">Sign in to your MRC Radiology account.</p>

      <Field
        id="mrc-auth-email"
        label="Email"
        icon={<MailIcon size={18} />}
        error={fieldErrors.email}
      >
        <input
          ref={emailRef}
          id="mrc-auth-email"
          name="email"
          type="email"
          className="mrc-auth__input browser-default"
          value={email}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          placeholder="name@example.com"
          autoComplete="username"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus={!focusPassword}
          readOnly={submitting}
          aria-invalid={fieldErrors.email ? true : undefined}
          aria-describedby={fieldErrors.email ? "mrc-auth-email-error" : undefined}
        />
      </Field>

      <Field
        id="mrc-auth-password"
        label="Password"
        icon={<LockIcon size={18} />}
        error={fieldErrors.password}
      >
        <input
          ref={passwordRef}
          id="mrc-auth-password"
          name="password"
          type={showPassword ? "text" : "password"}
          className="mrc-auth__input browser-default"
          value={password}
          onChange={handlePasswordChange}
          placeholder="Enter your password"
          autoComplete="current-password"
          autoFocus={focusPassword}
          readOnly={submitting}
          aria-invalid={fieldErrors.password ? true : undefined}
          aria-describedby={fieldErrors.password ? "mrc-auth-password-error" : undefined}
        />
        <button
          type="button"
          className="mrc-auth__toggle"
          onClick={() => setShowPassword((visible) => !visible)}
          onMouseDown={(event) => event.preventDefault()} // keep the caret in the field
          aria-label={showPassword ? "Hide password" : "Show password"}
          aria-pressed={showPassword}
        >
          {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
        </button>
      </Field>

      {formError && (
        <div className="mrc-auth__alert" role="alert">
          <AlertCircleIcon size={18} />
          <span>{formError}</span>
        </div>
      )}

      <button
        type="submit"
        className="mrc-auth__button"
        disabled={submitting}
        data-loading={submitting || undefined}
      >
        {submitting ? (
          <>
            <span className="mrc-auth__spinner" aria-hidden="true" />
            Signing in…
          </>
        ) : (
          "Sign in"
        )}
      </button>
    </form>
  );
}
